import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Sidebar from './components/Sidebar';
import Cart from './components/Cart';
import RouteTransition from './components/RouteTransition';
import Preloader from './components/Preloader';
import SessionWarning from './components/SessionWarning';

// Lazy load all page components
const HomePage = lazy(() => import('./pages/HomePage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const ItemDetailsPage = lazy(() => import('./pages/ItemDetailsPage'));
const AccessPage = lazy(() => import('./pages/AccessPage'));
const PartnershipPage = lazy(() => import('./pages/PartnershipPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const AllItemsPage = lazy(() => import('./pages/AllItemsPage'));
const EmailPage = lazy(() => import('./pages/EmailPage'));
const ShoppingPage = lazy(() => import('./pages/ShoppingPage'));
const ShoppingItemPage = lazy(() => import('./pages/ShoppingItemPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const BackofficeDashboard = lazy(() => import('./pages/BackofficeDashboard'));
const BackofficeProducts = lazy(() => import('./pages/BackofficeProducts'));
const BackofficeProductEdit = lazy(() => import('./pages/BackofficeProductEdit'));
const BackofficeOrders = lazy(() => import('./pages/BackofficeOrders'));
const BackofficeAnalytics = lazy(() => import('./pages/BackofficeAnalytics'));
const BackofficeOrderEdit = lazy(() => import('./pages/BackofficeOrderEdit'));
const BackofficeCatalog = lazy(() => import('./pages/BackofficeCatalog'));
const BackofficeCategoryEdit = lazy(() => import('./pages/BackofficeCategoryEdit'));
const BackofficeCatalogItemEdit = lazy(() => import('./pages/BackofficeCatalogItemEdit'));
const BackofficeLogin = lazy(() => import('./pages/BackofficeLogin'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleCart = useCallback(() => {
    setIsCartOpen(!isCartOpen);
  }, [isCartOpen]);

  // Listen for custom cart toggle events
  React.useEffect(() => {
    const handleToggleCart = () => {
      toggleCart();
    };

    window.addEventListener('toggleCart', handleToggleCart);
    return () => {
      window.removeEventListener('toggleCart', handleToggleCart);
    };
  }, [toggleCart]);

  // Use React.cloneElement more efficiently
  const childrenWithProps = React.isValidElement(children) 
    ? React.cloneElement(children as React.ReactElement<any>, { onToggleCart: toggleCart })
    : children;

  return (
    <>
      <div className="min-h-screen bg-primary">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} onToggleCart={toggleCart} />
        <Header onToggleSidebar={toggleSidebar} onToggleCart={toggleCart} />
        <main className="max-w-7xl mx-auto px-4 py-8 pt-48 md:pt-64">
          {childrenWithProps}
        </main>
        <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      </div>
      <Footer />
    </>
  );
};

const BackofficeLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const isCatalogSection = location.pathname.startsWith('/backoffice/catalog');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Persistent Sidebar */}
      <div className="fixed left-0 top-0 w-64 h-full bg-white shadow-lg border-r z-20">
        <div className="p-6">
          {/* Logo/Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">We Love Vilamoura</h1>
            <p className="text-sm text-gray-500">Backoffice</p>
          </div>


          {/* Navigation */}
          <nav className="space-y-2">
            <Link
              to="/backoffice"
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors group ${
                isActiveRoute('/backoffice') ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isActiveRoute('/backoffice') ? 'bg-orange-100' : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Dashboard</p>
                <p className="text-xs text-gray-500">Overview & Stats</p>
              </div>
            </Link>

            <Link
              to="/backoffice/products"
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors group ${
                isActiveRoute('/backoffice/products') ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isActiveRoute('/backoffice/products') ? 'bg-orange-100' : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Products</p>
                <p className="text-xs text-gray-500">Manage inventory</p>
              </div>
            </Link>

            <Link
              to="/backoffice/catalog"
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors group ${
                isCatalogSection ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isCatalogSection ? 'bg-orange-100' : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Catalog</p>
                <p className="text-xs text-gray-500">Categories &amp; listings</p>
              </div>
            </Link>

            <Link
              to="/backoffice/orders"
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors group ${
                isActiveRoute('/backoffice/orders') ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isActiveRoute('/backoffice/orders') ? 'bg-orange-100' : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Orders</p>
                <p className="text-xs text-gray-500">View & manage orders</p>
              </div>
            </Link>

            <Link
              to="/backoffice/analytics"
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors group ${
                isActiveRoute('/backoffice/analytics') ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isActiveRoute('/backoffice/analytics') ? 'bg-orange-100' : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Analytics</p>
                <p className="text-xs text-gray-500">Reports & insights</p>
              </div>
            </Link>
          </nav>

          {/* Logout Button */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={logout}
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-red-50 text-red-600 transition-colors group w-full"
            >
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Logout</p>
                <p className="text-xs text-red-500">Sign out</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64">
        <div className="p-8">
          {children}
        </div>
      </div>

      {/* Session Warning */}
      <SessionWarning />
    </div>
  );
};

export function AppRoutes() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Routes>
      {/* Main Site Routes */}
      <Route path="/*" element={
        <Layout>
          <RouteTransition>
            <Suspense fallback={<Preloader isLoading={true} />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/all" element={<AllItemsPage />} />
                <Route path="/shopping" element={<ShoppingPage />} />
                <Route path="/shopping/item/:id" element={<ShoppingItemPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/:categoryId" element={<CategoryPage />} />
                
                <Route path="/boat-tours/:id" element={<ItemDetailsPage category="boat-tours" />} />
                <Route path="/gastronomy/:id" element={<ItemDetailsPage category="gastronomy" />} />
                <Route path="/activities/:id" element={<ItemDetailsPage category="activities" />} />
                <Route path="/rent-a-car/:id" element={<ItemDetailsPage category="rent-a-car" />} />
                <Route path="/winery-tours/:id" element={<ItemDetailsPage category="winery-tours" />} />
                <Route path="/wellness/:id" element={<ItemDetailsPage category="wellness" />} />
                <Route path="/beauty-and-hair-salons/:id" element={<ItemDetailsPage category="beauty-and-hair-salons" />} />
                <Route path="/chef-at-home/:id" element={<ItemDetailsPage category="chef-at-home" />} />
                
                <Route path="/access" element={<AccessPage />} />
                <Route path="/partnership" element={<PartnershipPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<EmailPage />} />
              </Routes>
            </Suspense>
          </RouteTransition>
        </Layout>
      } />

      {/* Backoffice Login - No Sidebar */}
      <Route path="/backoffice/login" element={<BackofficeLogin />} />

      {/* Backoffice Routes - With Sidebar */}
      <Route path="/backoffice/*" element={
        <BackofficeLayout>
          <Suspense fallback={<Preloader isLoading={true} />}>
            <Routes>
              <Route path="/" element={
                <ProtectedRoute>
                  <BackofficeDashboard />
                </ProtectedRoute>
              } />
              <Route path="/products" element={
                <ProtectedRoute>
                  <BackofficeProducts />
                </ProtectedRoute>
              } />
              <Route path="/products/edit/:id" element={
                <ProtectedRoute>
                  <BackofficeProductEdit />
                </ProtectedRoute>
              } />
              {/* `:itemId` is "new" for create — do not add a separate /items/new route
                  or itemId will be missing and the form shows "Item not found". */}
              <Route path="/catalog/:categoryId/items/:itemId" element={
                <ProtectedRoute>
                  <BackofficeCatalogItemEdit />
                </ProtectedRoute>
              } />
              <Route path="/catalog/:categoryId" element={
                <ProtectedRoute>
                  <BackofficeCategoryEdit />
                </ProtectedRoute>
              } />
              <Route path="/catalog" element={
                <ProtectedRoute>
                  <BackofficeCatalog />
                </ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute>
                  <BackofficeOrders />
                </ProtectedRoute>
              } />
              <Route path="/orders/edit/:id" element={
                <ProtectedRoute>
                  <BackofficeOrderEdit />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <BackofficeAnalytics />
                </ProtectedRoute>
              } />
            </Routes>
          </Suspense>
        </BackofficeLayout>
      } />
    </Routes>
  );
} 